/**
 * useReviews.js
 *
 * Hook per la gestione delle recensioni nel sistema NoleggioinCloud.
 *
 * Struttura Firebase Realtime Database:
 *
 *   reviews/
 *     {companyId}/
 *       {reviewId}/
 *         bookingId       string   — ID della prenotazione (chiave di idempotenza)
 *         userId          string   — UID del cliente
 *         userName        string   — nome visualizzato
 *         rating          number   — 1-5 stelle
 *         titolo          string   — titolo breve
 *         testo           string   — testo completo
 *         status          string   — 'pending_filter' | 'approved' | 'rejected' | 'flagged'
 *         createdAt       string   — ISO timestamp
 *         filteredWords   string[] — parole trovate dal filtro (se status=rejected/flagged)
 *         rejectReason    string   — motivo rifiuto (platform admin o auto-filtro)
 *         flaggedAt       string   — ISO timestamp segnalazione
 *         flaggedBy       string   — UID di chi ha segnalato (admin noleggio)
 *         flagNote        string   — nota libera del segnalante
 *         resolvedAt      string   — ISO timestamp risoluzione
 *         resolvedBy      string   — UID platform admin che ha risolto
 *
 *   reviewsIndex/
 *     byBooking/
 *       {bookingId}: {companyId}/{reviewId}   — per sapere se esiste già una rec.
 *
 * Regole di accesso (sintetizzate):
 *   - Cliente: scrive solo sua recensione, legge le proprie
 *   - Admin noleggio: legge tutte della sua company, può segnalare (flag)
 *   - Platform admin: legge tutto, può approvare/rifiutare/resolvere
 *   - Pubblico: legge solo le `approved` (via companies/{id}/reviewsPublic — vedi sotto)
 */

import { useState, useEffect, useCallback } from 'react';
import {
  ref as dbRef,
  update,
  get,
  onValue,
  remove,
} from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { database, functions } from '../config/firebase';

// ─── Hook: submitReview ───────────────────────────────────────────────────────

/**
 * Usato dal cliente per inviare una nuova recensione.
 *
 * FIX (Agosto 2026, audit sicurezza): prima questo hook eseguiva il filtro
 * parolacce (checkBadWords) nel BROWSER e scriveva direttamente il record
 * recensione — incluso il campo `status` — su Realtime Database. Le Rules
 * verificavano solo che userId corrispondesse all'utente autenticato, non il
 * contenuto o la relazione con una prenotazione reale: bastava la console
 * del browser per pubblicare qualunque contenuto con status: 'approved',
 * bypassando il filtro, o per un bookingId non proprio/non concluso.
 *
 * Ora delega alla Cloud Function `submitReview`, che verifica lato server che
 * la prenotazione sia reale, del chiamante e conclusa, e applica lì il
 * filtro parolacce — il client non decide più da solo lo status.
 */
export function useSubmitReview() {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);

  const submitReview = useCallback(async ({
    companyId,
    bookingId,
    userName,
    rating,
    titolo,
    testo,
  }) => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const submitReviewFn = httpsCallable(functions, 'submitReview');
      const result = await submitReviewFn({ companyId, bookingId, userName, rating, titolo, testo });
      setSuccess(true);
      return result.data;
    } catch (err) {
      console.error('Errore submit recensione:', err);
      if (err.code === 'functions/already-exists') setError('Hai già lasciato una recensione per questa prenotazione.');
      else if (err.code === 'functions/failed-precondition') setError(err.message || 'Puoi lasciare una recensione solo dopo la riconsegna del veicolo.');
      else setError('Errore durante il salvataggio. Riprova tra qualche secondo.');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submitReview, loading, error, success };
}

// ─── Hook: useCompanyReviews ─────────────────────────────────────────────────

/**
 * Legge tutte le recensioni di una company (per admin del noleggio).
 * Usato in AdminDashboard → tab Recensioni.
 */
export function useCompanyReviews(companyId) {
  const [reviews, setReviews]   = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    const ref = dbRef(database, `reviews/${companyId}`);
    const unsubscribe = onValue(ref, (snap) => {
      if (!snap.exists()) { setReviews([]); setLoading(false); return; }
      const list = [];
      snap.forEach((child) => list.push({ id: child.key, ...child.val() }));
      // Più recenti prima
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setReviews(list);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [companyId]);

  return { reviews, loading };
}

// ─── Hook: usePublicReviews ───────────────────────────────────────────────────

/**
 * Legge le recensioni approved di una company.
 *
 * FIX (bug sicurezza/lettura pubblica): questo hook leggeva prima da
 * `reviews/{companyId}` filtrando `status === 'approved'`. Ma quel nodo
 * richiede `auth != null` nelle Rules (database.rules.json) — un visitatore
 * anonimo del sito pubblico (esattamente il pubblico per cui esiste
 * ReviewsSection.js) veniva sempre respinto con permission-denied, e la
 * sezione recensioni non compariva mai. Ora legge da `reviewsPublic/{companyId}`,
 * un nodo leggibile da chiunque, in cui la Cloud Function submitReview (e le
 * azioni admin qui sotto) specchiano SOLO le recensioni con status 'approved'.
 *
 * @param {string} companyId
 * @param {boolean} realtime - true = onValue (listener live, usare sul sito del
 *   singolo noleggio dove le recensioni possono arrivare mentre l'utente è sulla
 *   pagina). false = get() una tantum (usare in SearchCompanies/directory dove
 *   molte card sono visibili contemporaneamente — evita N connessioni WebSocket
 *   aperte simultaneamente che aumentano inutilmente costi e banda Firebase).
 */
export function usePublicReviews(companyId, realtime = true) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }
    const ref = dbRef(database, `reviewsPublic/${companyId}`);

    const handleSnap = (snap) => {
      if (!snap.exists()) { setReviews([]); setLoading(false); return; }
      const list = [];
      snap.forEach((child) => list.push({ id: child.key, ...child.val() }));
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setReviews(list);
      setLoading(false);
    };

    if (realtime) {
      // Listener live — aggiornamento in tempo reale (sito singolo noleggio)
      const unsubscribe = onValue(ref, handleSnap);
      return () => unsubscribe();
    } else {
      // Lettura una tantum — nessun listener persistente (directory /cerca)
      get(ref).then(handleSnap).catch(() => setLoading(false));
    }
  }, [companyId, realtime]);

  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return { reviews, loading, avgRating, count: reviews.length };
}

// ─── Hook: useUserReviews ────────────────────────────────────────────────────

/**
 * Legge tutte le recensioni di un utente (per area cliente → storico).
 * Deve leggere reviewsIndex per trovare le sue recensioni in tutte le companies.
 */
export function useUserReviews(userId) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) { setLoading(false); return; }

    // Legge l'indice delle recensioni dell'utente
    const idxRef = dbRef(database, `reviewsIndex/byUser/${userId}`);
    const unsubscribe = onValue(idxRef, async (snap) => {
      if (!snap.exists()) { setReviews([]); setLoading(false); return; }

      const entries = [];
      snap.forEach((child) => entries.push({ bookingId: child.key, ...child.val() }));

      // Carica ogni recensione completa
      const results = await Promise.all(
        entries.map(async (entry) => {
          try {
            const revSnap = await get(
              dbRef(database, `reviews/${entry.companyId}/${entry.reviewId}`)
            );
            if (!revSnap.exists()) return null;
            return { id: entry.reviewId, bookingId: entry.bookingId, companyId: entry.companyId, ...revSnap.val() };
          } catch {
            return null;
          }
        })
      );

      const list = results.filter(Boolean);
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setReviews(list);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  return { reviews, loading };
}

// ─── Hook: useBookingReviewStatus ────────────────────────────────────────────

/**
 * Controlla se esiste già una recensione per un bookingId.
 * Usato per mostrare/nascondere il bottone "Lascia una recensione".
 */
export function useBookingReviewStatus(bookingId) {
  const [status, setStatus] = useState(null); // null = non ancora caricato
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!bookingId) { setLoading(false); return; }
    get(dbRef(database, `reviewsIndex/byBooking/${bookingId}`)).then((snap) => {
      setStatus(snap.exists() ? snap.val().status : 'none');
      setLoading(false);
    }).catch(() => {
      setStatus('none');
      setLoading(false);
    });
  }, [bookingId]);

  return { status, loading };
}

// ─── Hook: useFlaggedReviews (Platform Admin) ─────────────────────────────────

/**
 * Legge tutte le recensioni con status 'flagged' su tutta la piattaforma.
 * Usato dal Platform Admin Panel.
 */
export function useFlaggedReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Legge l'indice globale delle segnalazioni
    const ref = dbRef(database, 'reviewsIndex/flagged');
    const unsubscribe = onValue(ref, async (snap) => {
      if (!snap.exists()) { setReviews([]); setLoading(false); return; }

      const entries = [];
      snap.forEach((child) => entries.push({ key: child.key, ...child.val() }));

      const results = await Promise.all(
        entries.map(async (entry) => {
          try {
            const revSnap = await get(dbRef(database, `reviews/${entry.companyId}/${entry.reviewId}`));
            if (!revSnap.exists()) return null;
            return {
              id: entry.reviewId,
              companyId: entry.companyId,
              companyName: entry.companyName || entry.companyId,
              ...revSnap.val(),
            };
          } catch {
            return null;
          }
        })
      );

      const list = results.filter(Boolean);
      list.sort((a, b) => new Date(b.flaggedAt || b.createdAt) - new Date(a.flaggedAt || a.createdAt));
      setReviews(list);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { reviews, loading };
}

// ─── Azioni ──────────────────────────────────────────────────────────────────

/**
 * Segna una recensione come "flagged" (segnalata dal proprietario del noleggio).
 */
export async function flagReview({ companyId, reviewId, flaggedBy, flagNote, companyName }) {
  const now = new Date().toISOString();
  await update(dbRef(database, `reviews/${companyId}/${reviewId}`), {
    status: 'flagged',
    flaggedAt: now,
    flaggedBy,
    flagNote: flagNote || '',
  });
  // Aggiorna indice segnalate (per Platform Admin)
  await update(dbRef(database, `reviewsIndex/flagged/${reviewId}`), {
    companyId,
    reviewId,
    companyName: companyName || companyId,
    flaggedAt: now,
  });
  // FIX: rimuove la recensione dallo specchio pubblico (reviewsPublic) mentre
  // è sotto segnalazione — prima restava visibile sul sito pubblico anche
  // durante la revisione da parte del platform admin.
  await remove(dbRef(database, `reviewsPublic/${companyId}/${reviewId}`)).catch(() => {});
  // Aggiorna anche indice byBooking
  // (non conosciamo il bookingId qui, ma non serve: il Platform Admin usa l'indice flagged)
}

/**
 * Risolve una recensione segnalata (Platform Admin):
 *   action = 'approve' → status: 'approved'
 *   action = 'reject'  → status: 'rejected' + rejectReason
 */
export async function resolveReview({ companyId, reviewId, action, rejectReason, resolvedBy, bookingId }) {
  const now = new Date().toISOString();
  const newStatus = action === 'approve' ? 'approved' : 'rejected';

  await update(dbRef(database, `reviews/${companyId}/${reviewId}`), {
    status: newStatus,
    resolvedAt: now,
    resolvedBy,
    ...(newStatus === 'rejected' ? { rejectReason: rejectReason || 'Recensione rimossa dalla piattaforma.' } : {}),
  });

  // FIX: tiene sincronizzato lo specchio pubblico (reviewsPublic) con l'esito
  // della revisione — se riapprovata la ripubblica, se respinta la rimuove
  // (flagReview l'ha già tolta, ma lo garantiamo comunque anche qui).
  if (newStatus === 'approved') {
    const snap = await get(dbRef(database, `reviews/${companyId}/${reviewId}`));
    if (snap.exists()) {
      const r = snap.val();
      await update(dbRef(database, `reviewsPublic/${companyId}/${reviewId}`), {
        rating: r.rating,
        titolo: r.titolo,
        testo: r.testo,
        userName: r.userName,
        createdAt: r.createdAt,
      });
    }
  } else {
    await remove(dbRef(database, `reviewsPublic/${companyId}/${reviewId}`)).catch(() => {});
  }

  // Rimuove dall'indice flagged
  await update(dbRef(database, `reviewsIndex/flagged/${reviewId}`), null);

  // Aggiorna indice byBooking se disponibile
  if (bookingId) {
    await update(dbRef(database, `reviewsIndex/byBooking/${bookingId}`), { status: newStatus });
  }
}
